import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import type { Spell, SpellSchool } from '@character-creator/types';
import { useRulesetSpells, useRulesetClasses } from '../hooks/useRulesetData';
import { LinkedText } from '../components/LinkedText';

const SPELL_SCHOOLS: SpellSchool[] = [
  'abjuration',
  'conjuration',
  'divination',
  'enchantment',
  'evocation',
  'illusion',
  'necromancy',
  'transmutation',
];

const SPELL_LEVELS = [
  { value: -1, label: 'All Levels' },
  { value: 0, label: 'Cantrip' },
  { value: 1, label: '1st Level' },
  { value: 2, label: '2nd Level' },
  { value: 3, label: '3rd Level' },
  { value: 4, label: '4th Level' },
  { value: 5, label: '5th Level' },
  { value: 6, label: '6th Level' },
  { value: 7, label: '7th Level' },
  { value: 8, label: '8th Level' },
  { value: 9, label: '9th Level' },
];

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatSpellLevel(level: number): string {
  if (level === 0) return 'Cantrip';
  if (level === 1) return '1st';
  if (level === 2) return '2nd';
  if (level === 3) return '3rd';
  return `${level}th`;
}

function getComponentsString(spell: Spell): string {
  const parts: string[] = [];
  if (spell.components.verbal) parts.push('V');
  if (spell.components.somatic) parts.push('S');
  if (spell.components.material) parts.push('M');
  return parts.join(', ');
}

export function SpellsReferencePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<number>(-1);
  const [schoolFilter, setSchoolFilter] = useState<SpellSchool | 'all'>('all');
  const [classFilter, setClassFilter] = useState<string>('all');
  const [ritualOnly, setRitualOnly] = useState(false);
  const [concentrationOnly, setConcentrationOnly] = useState(false);
  const [expandedSpellId, setExpandedSpellId] = useState<string | null>(null);

  const spells = useRulesetSpells();
  const classes = useRulesetClasses();
  const spellcastingClasses = useMemo(
    () => classes.filter((c) => c.spellcasting !== null),
    [classes]
  );

  const filteredSpells = useMemo(() => {
    return spells.filter((spell) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!spell.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Level filter
      if (levelFilter !== -1 && spell.level !== levelFilter) {
        return false;
      }

      // School filter
      if (schoolFilter !== 'all' && spell.school !== schoolFilter) {
        return false;
      }

      // Class filter
      if (classFilter !== 'all' && !spell.classes.includes(classFilter)) {
        return false;
      }

      // Ritual filter
      if (ritualOnly && !spell.ritual) {
        return false;
      }

      // Concentration filter
      if (concentrationOnly && !spell.concentration) {
        return false;
      }

      return true;
    });
  }, [spells, searchQuery, levelFilter, schoolFilter, classFilter, ritualOnly, concentrationOnly]);

  const toggleSpellExpanded = (spellId: string) => {
    setExpandedSpellId((prev) => (prev === spellId ? null : spellId));
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-background-card/95 backdrop-blur-sm border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/create"
              className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-background-elevated transition-colors"
            >
              <BackIcon className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-semibold text-text-primary">Spells Reference</h1>
          </div>
        </div>
      </header>

      {/* Search & Filters */}
      <div className="sticky top-[65px] z-10 bg-background-primary border-b border-border-default">
        <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
          {/* Search Input */}
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search spells..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-background-card border border-border-default rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-gold"
            />
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap gap-2">
            {/* Level Filter */}
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(Number(e.target.value))}
              className="px-3 py-2 bg-background-card border border-border-default rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-gold"
            >
              {SPELL_LEVELS.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>

            {/* School Filter */}
            <select
              value={schoolFilter}
              onChange={(e) => setSchoolFilter(e.target.value as SpellSchool | 'all')}
              className="px-3 py-2 bg-background-card border border-border-default rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-gold"
            >
              <option value="all">All Schools</option>
              {SPELL_SCHOOLS.map((school) => (
                <option key={school} value={school}>
                  {capitalizeFirst(school)}
                </option>
              ))}
            </select>

            {/* Class Filter */}
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="px-3 py-2 bg-background-card border border-border-default rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-gold"
            >
              <option value="all">All Classes</option>
              {spellcastingClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>

            {/* Ritual Toggle */}
            <button
              onClick={() => setRitualOnly(!ritualOnly)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                ritualOnly
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-card border border-border-default text-text-secondary hover:text-text-primary'
              }`}
            >
              Ritual
            </button>

            {/* Concentration Toggle */}
            <button
              onClick={() => setConcentrationOnly(!concentrationOnly)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                concentrationOnly
                  ? 'bg-accent-gold text-background-primary'
                  : 'bg-background-card border border-border-default text-text-secondary hover:text-text-primary'
              }`}
            >
              Concentration
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <p className="text-sm text-text-muted">
          Showing {filteredSpells.length} of {spells.length} spells
        </p>
      </div>

      {/* Spell List */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {filteredSpells.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-card flex items-center justify-center border border-border-default">
              <SpellBookIcon className="w-8 h-8 text-text-muted" />
            </div>
            <p className="text-text-secondary">No spells match your filters.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setLevelFilter(-1);
                setSchoolFilter('all');
                setClassFilter('all');
                setRitualOnly(false);
                setConcentrationOnly(false);
              }}
              className="mt-4 px-4 py-2 text-accent-gold hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredSpells.map((spell) => (
              <SpellCard
                key={spell.id}
                spell={spell}
                isExpanded={expandedSpellId === spell.id}
                onToggle={() => toggleSpellExpanded(spell.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

interface SpellCardProps {
  spell: Spell;
  isExpanded: boolean;
  onToggle: () => void;
}

function SpellCard({ spell, isExpanded, onToggle }: SpellCardProps) {
  return (
    <div
      id={spell.id}
      className="bg-background-card border border-border-default rounded-lg overflow-hidden"
    >
      {/* Collapsed View */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left hover:bg-background-elevated transition-colors"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-text-primary">{spell.name}</h3>
              {spell.ritual && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-purple/20 text-accent-purple rounded">
                  Ritual
                </span>
              )}
              {spell.concentration && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-accent-blue/20 text-accent-blue rounded">
                  Conc.
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-text-muted">
              {getComponentsString(spell)} &bull; {spell.castingTime} &bull; {spell.range} &bull;{' '}
              {spell.duration}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-medium text-accent-gold">
              {formatSpellLevel(spell.level)}
            </div>
            <div className="text-xs text-text-muted">{capitalizeFirst(spell.school)}</div>
          </div>
        </div>
        <div className="flex items-center justify-center mt-2">
          <ChevronIcon
            className={`w-5 h-5 text-text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Expanded View */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border-default">
          <div className="pt-4 space-y-4">
            {/* Description */}
            <div>
              <p className="text-text-secondary text-sm leading-relaxed">
                <LinkedText excludePages={['spells']}>{spell.description}</LinkedText>
              </p>
            </div>

            {/* Higher Level */}
            {spell.higherLevel && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-1">At Higher Levels</h4>
                <p className="text-text-secondary text-sm leading-relaxed">
                  <LinkedText excludePages={['spells']}>{spell.higherLevel}</LinkedText>
                </p>
              </div>
            )}

            {/* Material Components */}
            {spell.components.material && spell.components.materialDescription && (
              <div>
                <h4 className="text-sm font-semibold text-text-primary mb-1">Material Components</h4>
                <p className="text-text-secondary text-sm">
                  {capitalizeFirst(spell.components.materialDescription)}
                  {spell.components.materialCost && (
                    <span className="text-accent-gold ml-1">
                      ({spell.components.materialCost} gp
                      {spell.components.materialConsumed && ', consumed'})
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Classes */}
            <div>
              <h4 className="text-sm font-semibold text-text-primary mb-2">Available to</h4>
              <div className="flex flex-wrap gap-1.5">
                {spell.classes.map((classId) => (
                  <Link
                    key={classId}
                    to={`/classes#${classId}`}
                    className="px-2 py-1 text-xs font-medium bg-background-elevated text-text-secondary rounded hover:bg-background-secondary hover:text-accent-gold transition-colors"
                  >
                    {capitalizeFirst(classId)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>
  );
}

function SpellBookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}
